/**
 * Where the person is, while the app is open (v1's `locationTracker.js`):
 * app-wide, not the map's, so fixes flow whichever widget is on screen. A fix
 * is posted on a 50 m move or once a minute at rest — sitting still otherwise
 * posted thousands of identical rows a night. A browser stops this when the
 * page is hidden; the Android app is the always-on source (F6).
 *
 * The switch is per browser and per person (`prefs/store.ts`). **It comes
 * back on by itself when the browser already holds the permission** (v1.82):
 * a cache clear forgets the setting, not the grant.
 *
 * **A failed read says so** (v1.81; 31A): a refusal turns the switch off; a
 * fix that times out or cannot be had keeps watching, and the map shows why
 * until the next fix lands.
 */
import { create } from "zustand";

import { api } from "../api/client";

const MIN_INTERVAL_MS = 60_000;
const MIN_DISTANCE_M = 50;

export interface Fix {
  lat: number;
  lng: number;
  accuracy: number;
  at: number;
}

/** The latest fix, for the map's live dot, and why the last read failed. */
export const useLiveLocation = create<{ fix: Fix | null; error: string | null }>()(() => ({
  fix: null,
  error: null,
}));

/** Why a read failed, in words for the person. */
export function readFailure(code: number): string {
  if (code === 1) return "Location access was refused, so tracking is off.";
  if (code === 3) return "No location fix yet: the device took too long to answer.";
  return "No location fix: the device cannot find where it is.";
}

export function metresBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Post this fix? On the first, after a minute, or after a 50 m move. */
export function shouldPost(last: Fix | null, next: Fix): boolean {
  if (!last) return true;
  if (next.at - last.at >= MIN_INTERVAL_MS) return true;
  return metresBetween(last, next) >= MIN_DISTANCE_M;
}

let watch: number | null = null;
let posted: Fix | null = null;

export function startTracking(onDenied: () => void): void {
  if (!navigator.geolocation || watch !== null) return;
  watch = navigator.geolocation.watchPosition(
    (position) => {
      const fix: Fix = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        at: Date.now(),
      };
      useLiveLocation.setState({ fix, error: null });
      if (!shouldPost(posted, fix)) return;
      posted = fix;
      void api
        .POST("/api/map/location", {
          body: { lat: fix.lat, lng: fix.lng, accuracy_m: fix.accuracy },
        })
        .catch(() => undefined);
    },
    (failure) => {
      const error = readFailure(failure.code);
      if (failure.code !== 1) {
        useLiveLocation.setState({ error });
        return;
      }
      stopTracking();
      useLiveLocation.setState({ error });
      onDenied();
    },
    { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
  );
}

export function stopTracking(): void {
  if (watch !== null) navigator.geolocation.clearWatch(watch);
  watch = null;
  posted = null;
  useLiveLocation.setState({ fix: null, error: null });
}

/** Whether the browser already holds the permission, without asking for it. */
export async function granted(): Promise<boolean> {
  try {
    const status = await navigator.permissions?.query({ name: "geolocation" });
    return status?.state === "granted";
  } catch {
    return false;
  }
}
